import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import ventasSeguimientoService, {
  VentaHiloComprobante,
  formatComprobanteVentaHiloLine,
} from '../../services/ventasSeguimientoService';
import { getApiErrorMessage } from '../../utils/apiError';

interface Props {
  open: boolean;
  onClose: () => void;
  nroFormularioMaster: number | null;
  nroItemMaster: number | null;
  resumenLinea?: string;
}

const tipoFormKey = (t?: string | number | null): string =>
  t === null || t === undefined || t === '' ? '' : String(t).trim();

const isFactura = (t?: string | number | null) => {
  const k = tipoFormKey(t);
  return k === '1' || k === '2';
};
const isNc = (t?: string | number | null) => tipoFormKey(t) === '3';
const isRemito = (t?: string | number | null) => tipoFormKey(t) === '9';
const isPedido = (t?: string | number | null) => tipoFormKey(t) === '12';
const isOrdenDevolucion = (t?: string | number | null) => tipoFormKey(t) === '45';

const formatFecha = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('es-AR');
};

const VentaPendienteHiloInfoDialog: React.FC<Props> = ({
  open,
  onClose,
  nroFormularioMaster,
  nroItemMaster,
  resumenLinea,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<VentaHiloComprobante[]>([]);

  useEffect(() => {
    if (!open || nroFormularioMaster == null || nroItemMaster == null) {
      setItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await ventasSeguimientoService.getHilo(nroFormularioMaster, nroItemMaster);
        if (!cancelled) setItems(data.comprobantes || []);
      } catch (e) {
        if (!cancelled) {
          enqueueSnackbar(getApiErrorMessage(e, 'No se pudo cargar el detalle del hilo'), { variant: 'error' });
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, nroFormularioMaster, nroItemMaster, enqueueSnackbar]);

  const pedidoLines = items.filter((c) => isPedido(c.nroTipoFormulario));
  const facturados = items.filter((c) => isFactura(c.nroTipoFormulario));
  const notasCredito = items.filter((c) => isNc(c.nroTipoFormulario));
  const ordenesDevolucion = items.filter((c) => isOrdenDevolucion(c.nroTipoFormulario));
  const remitidos = items.filter((c) => isRemito(c.nroTipoFormulario));

  const renderBlock = (title: string, rows: VentaHiloComprobante[], emptyMsg: string) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1976d2' }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {emptyMsg}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Cantidad
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={`${c.nroFormulario}-${c.nroItem}-${c.nroTipoFormulario}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatFecha(c.fechaEmision)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteVentaHiloLine(c)}</TableCell>
                  <TableCell align="right">{c.cantidad}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Facturado, remitido y devoluciones (detalle)</DialogTitle>
      <DialogContent>
        {resumenLinea ? (
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            {resumenLinea}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Línea master {nroFormularioMaster}/{nroItemMaster}
        </Typography>
        {loading ? (
          <CircularProgress size={24} sx={{ display: 'block', my: 2 }} />
        ) : (
          <>
            {renderBlock('Pedido', pedidoLines, 'Sin pedido en el hilo.')}
            {renderBlock('Ya facturado (facturas)', facturados, 'No hay facturación registrada en esta línea.')}
            {renderBlock('Notas de crédito', notasCredito, 'Sin notas de crédito vinculadas.')}
            {renderBlock('Órdenes de devolución', ordenesDevolucion, 'Sin órdenes de devolución vinculadas.')}
            {renderBlock('Ya remitido (remitos)', remitidos, 'Sin remitos registrados en esta línea.')}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VentaPendienteHiloInfoDialog;
